import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Paper,
  Pagination,
  ScrollArea,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconBan,
  IconDots,
  IconEdit,
  IconFileText,
  IconMapPin,
  IconAlertCircle,
  IconNote,
  IconPill,
  IconPlus,
  IconRefresh,
  IconShield,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import {
  applyComposedNoteAuthorship,
  COMPOSED_NOTE_TITLE_LINK_ID,
  composedChartNoteTag,
} from '../../../lib/chart/composedChartNote';
import { getPatientAccountRefs } from '../../../lib/clientFacingTasks';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { practitionerPreferredReference } from '../../../lib/loadPractitionerPreferredLabels';
import { formatMedplumClientError } from '../../appointments/utils/medplumClientErrors';
import type { EpisodeChartInteraction } from '../../../hooks/useEpisodeScopedClientChartBundle';
import {
  filterContactRowsToEpisodeRelated,
  useMedplumPatientContacts,
} from '../../../hooks/useMedplumPatientContacts';
import {
  canAuthorClinicalDocumentationForEpisode,
  chartScopeFromRoleAndPractitioner,
  getAppointmentChartAccess,
  getAssessmentChartAccess,
  isEpisodeScopedRowVisible,
} from '../../../lib/rbac/eocChartScope';
import { DateRangeSelect } from '../../../components/DateRangeSelect';
import { FilterListDrawer } from '../../../components/FilterListDrawer';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { getStatusColor, formatStatusText } from '../../../lib/statusUtils';
import { getMockNoteEntryForAppointment } from '../../appointments/mockAppointmentNoteData';
import { buildMockAppointmentPageState } from '../../appointments/mockAppointmentPageData';
import type { Client } from '../../caseload/data';
import { QRDocumentWithComments } from '../../supervision/components/QRDocumentWithComments';
import { ROWS_PER_PAGE_OPTIONS } from '../../caseload/data';
import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import { DoseSpotMedicationShell } from '../components/DoseSpotMedicationShell';
import type { DoseSpotDemoCompletePayload, DoseSpotEmbedIntent } from '../components/dosespotTypes';
import { LegacyAddMedicationModal } from '../components/LegacyAddMedicationModal';
import { ClientChartContactsPanel } from '../components/ClientChartContactsPanel';
import { useTreatmentPlanChartDocumentIds } from '../../../hooks/useChartEpisodeTreatmentPlan';
import { resolveDefaultTreatmentPlanTemplate } from '../../../lib/chart/chartDocumentTemplates';
import {
  searchPsychotherapyNotesForPatient,
  toPsychotherapyNoteRow,
} from '../../../lib/psychotherapyNotes';
import { openHistoricalPdfInNewTab } from '@/lib/openHistoricalPdf';
import { downloadDocumentAttachment } from '@/lib/adminActionsService';
import { ClientContactFormModal } from '../components/ClientContactFormModal';
import type { Contact } from '../data/contactTypes';
import { ChartRecordCard, NoteDetailView } from '../components';
import {
  hasRenderableSessionNoteContent,
  resolveChartRecordPresentation,
  resolveChartRecordTarget,
  type ChartRecordPresentation,
  type ChartRecordTargetIds,
} from '../utils/chartRecordPresentation';
import { GroupSharedNoteLink } from '../components/GroupSharedNoteLink';
import { getChartNoteTemplateById } from '../data/chartNoteTemplates';
import { useResolvedTemplates } from '../../settings/template-library/hooks';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '../../../lib/caseloadFhirConstants';
import { canAuthorClinicalNote, canCreateAdminNote } from '../../../lib/chart/chartDocumentAccess';
import {
  DEFAULT_DISPLAY_FILTER,
  MEDICAL_RECORD_DISPLAY_FILTER,
  shouldShowWhenHistoricalFilter,
  timelineShowsHistorical,
} from '../utils/medicalRecordTimelineFilters';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useSupervisionScope } from '../../supervision/hooks/useSupervisionScope';
import { AddOtherDocumentModal } from '../components/AddOtherDocumentModal';
import { getSectionsForNoteType } from '../data/noteTypeSections';
import {
  AddAdminNoteFormContent,
  AddChartNoteFormContent,
} from './MedicalRecordAddNoteForms';
import type {
  AdminNote,
  Appointment,
  ClinicalNote,
  DischargeSummary,
  Episode,
  EpisodeParty,
  HistoricalChartRecord,
  Medication,
} from '../data';
import {
  getRecordTypesForCategories,
  isSessionDocumentationRecordType,
  normalizeMedicalRecordType,
  MEDICAL_RECORD_TYPE_OPTIONS,
} from '../data/medicalRecordTypes';
import {
  contactRowHasGuarantorRole,
  type ClientOverviewContactRow,
} from '../utils/getClientOverviewContacts';
import {
  isFutureAppointment,
  parseDateLocal,
  PENDING_DOCUMENTATION_LABEL,
} from '../utils/appointmentPostSession';
import {
  deriveAppointmentDocumentationStatus,
  noteMatchesDocumentationStatusFilter,
} from '../../appointments/utils/documentationStatus';

/** Set `true` to restore the manual add-medication form instead of the DoseSpot shell. */
const USE_LEGACY_ADD_MEDICATION_FORM = false;

/** AdminNote.type to Medical Record filter type when recordType is not set. */
function adminTypeToRecordType(type: AdminNote['type']): string {
  switch (type) {
    case 'Admin':
      return 'Admin Notes';
    case 'Client Communication':
      return 'Contact Logs';
    case 'Internal':
      return 'Admin Notes';
    case 'Reminder':
      return 'Admin Notes';
    default:
      return 'Admin Notes';
  }
}

type TimelineItem =
  | { kind: 'appointment'; data: Appointment }
  | { kind: 'clinical_note'; data: ClinicalNote }
  | { kind: 'admin_note'; data: AdminNote }
  | { kind: 'discharge_summary'; data: DischargeSummary }
  | { kind: 'assessment'; data: ClinicalNote }
  | { kind: 'historical_record'; data: HistoricalChartRecord };

function getRecordTypeForItem(
  item: TimelineItem,
  notesByAppointmentId: Map<string, ClinicalNote>
): string | undefined {
  switch (item.kind) {
    case 'appointment': {
      const apt = item.data;
      if (apt.recordType) return apt.recordType;
      const linked = notesByAppointmentId.get(apt.id);
      if (linked?.noteType) return normalizeMedicalRecordType(linked.noteType) ?? linked.noteType;
      return undefined;
    }
    case 'clinical_note':
      return normalizeMedicalRecordType(item.data.noteType) ?? item.data.noteType;
    case 'admin_note':
      return item.data.recordType ?? adminTypeToRecordType(item.data.type);
    case 'discharge_summary':
      return 'Discharge Summary';
    case 'assessment':
      return 'Assessments';
    case 'historical_record':
      return normalizeMedicalRecordType(item.data.recordType) ?? item.data.recordType;
  }
}

/**
 * Inline note body. Prefers the real QuestionnaireResponse answers (`contentSections`) and falls
 * back to the note-type SOAP layout for records that predate structured extraction.
 */
function NoteBodySections({ note }: { note: ClinicalNote | undefined }): JSX.Element {
  const sections =
    note?.contentSections && note.contentSections.length > 0
      ? note.contentSections
      : getSectionsForNoteType(note?.noteType).sections.map(({ label, key }) => ({
        label,
        text: note?.soapNote?.[key] ?? '',
      }));
  return (
    <Stack gap="xs">
      {sections
        .filter((section) => section.text.trim().length > 0)
        .map((section) => (
          <div key={section.label}>
            <Text size="xs" c="gray.6" fw={600} tt="uppercase" mb={4}>{section.label}</Text>
            <Text size="sm" c="gray.7" style={{ whiteSpace: 'pre-wrap' }}>{section.text}</Text>
          </div>
        ))}
    </Stack>
  );
}

/** Stable per-row key — also used to track which record is expanded across filtering/pagination. */
function getTimelineItemKey(item: TimelineItem): string {
  switch (item.kind) {
    case 'appointment':
      return `apt-${item.data.id}`;
    case 'clinical_note':
      return `note-${item.data.id}`;
    case 'admin_note':
      return `admin-${item.data.id}`;
    case 'discharge_summary':
      return `ds-${item.data.id}`;
    case 'assessment':
      return `assess-${item.data.id}`;
    case 'historical_record':
      return `hist-${item.data.id}`;
  }
}

interface MedicalRecordTabProps {
  client: Client;
  appointments: Appointment[];
  notes: ClinicalNote[];
  adminNotes?: AdminNote[];
  episodes?: Episode[];
  medications?: Medication[];
  dischargeSummaries?: DischargeSummary[];
  historicalRecords?: HistoricalChartRecord[];
  /** When set (e.g. from Caseload episode link), Episode of Care filter is initialized to this episode ID. */
  initialEpisodeId?: string;
  /** Medplum pilot: lists all episode rows; drill-in (appointment, EoC page, note modals) uses this scope. */
  episodeInteraction?: EpisodeChartInteraction;
  /** Reloads the chart bundle after a note is persisted so the server row replaces the optimistic one. */
  onRefetch?: () => void;
}

function formatDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    return parseDateLocal(dateString).toLocaleDateString('en-US');
  } catch {
    return dateString;
  }
}


function getItemDate(item: TimelineItem): Date {
  if (item.kind === 'appointment') return parseDateLocal(item.data.date);
  if (item.kind === 'clinical_note') return parseDateLocal(item.data.dateOfService);
  if (item.kind === 'assessment') return parseDateLocal(item.data.dateOfService);
  if (item.kind === 'discharge_summary') return parseDateLocal(item.data.dischargeDate);
  if (item.kind === 'historical_record') return parseDateLocal(item.data.dateOfService);
  return new Date(item.data.createdAt);
}

/** Full date-time for timeline ordering (date primary, time secondary within the same day). */
function getTimelineSortTime(item: TimelineItem): number {
  if (item.kind === 'clinical_note' || item.kind === 'assessment') {
    const note = item.data;
    if (note.lastModified) return new Date(note.lastModified).getTime();
    if (note.signedDate?.includes('T')) return new Date(note.signedDate).getTime();
    return parseDateLocal(note.dateOfService).getTime();
  }
  if (item.kind === 'appointment') {
    const apt = item.data;
    if (apt.date && apt.time) {
      const combined = new Date(`${apt.date} ${apt.time}`);
      if (!Number.isNaN(combined.getTime())) return combined.getTime();
    }
    return parseDateLocal(apt.date).getTime();
  }
  if (item.kind === 'discharge_summary') {
    const ds = item.data;
    if (ds.signedAt?.includes('T')) return new Date(ds.signedAt).getTime();
    return parseDateLocal(ds.dischargeDate).getTime();
  }
  if (item.kind === 'historical_record') {
    return parseDateLocal(item.data.dateOfService).getTime();
  }
  return new Date(item.data.createdAt).getTime();
}

const RECORD_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

/** Extract only the time portion from an ISO string for the record header. */
function formatTimeOnly(isoString: string | undefined): string {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleString('en-US', RECORD_TIME_OPTIONS);
  } catch {
    return '';
  }
}

/** Return the time string for an appointment record (prefers explicit time, falls back to parsing). */
function formatRecordTime(_dateString: string, timeString?: string): string {
  if (timeString?.trim()) return timeString.trim();
  if (!_dateString) return '';
  try {
    const d = new Date(_dateString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', RECORD_TIME_OPTIONS);
  } catch {
    return '';
  }
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

///** Build option label for an Episode of Care (name + date range so same service can appear twice). */
//function getEpisodeOptionLabel(episode: Episode): string {
//  const start = formatDate(episode.startDate);
//  if (episode.endDate) {
//    return `${episode.name} (${start} – ${formatDate(episode.endDate)})`;
//  }
//  return `${episode.name} (Started ${start})`;
const FULL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

function formatFullDate(date: Date): string {
  return date.toLocaleDateString('en-US', FULL_DATE_OPTIONS);
}

/** Status badge for note statuses that aren't already handled by appointment logic. */
function getNoteStatusBadge(noteStatus: ClinicalNote['noteStatus']): JSX.Element | null {
  switch (noteStatus) {
    case 'Signed':
      return <Badge color="blue" variant="light" size="sm">Signed</Badge>;
    case 'Complete':
      return <Badge color="green" variant="light" size="sm">Complete</Badge>;
    case 'Draft':
      return <Badge color="yellow" variant="light" size="sm">Draft</Badge>;
    case 'Locked':
      return <Badge color="gray" variant="light" size="sm">Locked</Badge>;
    case 'Pending co-signature':
      return <Badge color="orange" variant="light" size="sm">Pending co-sig</Badge>;
    case 'Private':
      return <Badge color="gray" variant="light" size="sm">Private</Badge>;
    default:
      return null;
  }
}

const MOCK_STATE_KEY = 'mockAppointmentData';

/** Find the active Questionnaire template for a given note kind from resolved or mock templates. */
function getMedicalRecordTemplate(
  kind: 'chart' | 'admin',
  resolvedTemplates?: Questionnaire[],
): Questionnaire | null {
  const keywords: Record<typeof kind, string[]> = {
    chart: ['chart note', 'chart notes'],
    admin: ['admin note', 'admin notes'],
  };
  const terms = keywords[kind];

  // Prefer resolved templates from the shared resolver (settings-aware)
  if (resolvedTemplates && resolvedTemplates.length > 0) {
    const match = resolvedTemplates.find(
      (q) => terms.some((term) => (q.title ?? '').toLowerCase().includes(term)),
    );
    if (match) return match;
  }

  return null;
}

/**
 * Build a minimal QuestionnaireResponse that represents a standalone clinical note.
 * Items are derived from the soapNote fields mapped to the template's linkIds where possible.
 */
function buildNoteQuestionnaireResponse(opts: {
  kind: 'chart' | 'admin';
  template: Questionnaire | null;
  clientId: string;
  episodeId?: string;
  content: {
    title?: string;
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
    body?: string;
  };
}): QuestionnaireResponse {
  const { kind, template, clientId, episodeId, content } = opts;

  const canonicalRef = template
    ? (template.url && template.version
        ? `${template.url}|${template.version}`
        : `Questionnaire/${template.id}`)
    : undefined;

  // Map content fields to QR items using template linkIds where known
  const items: QuestionnaireResponse['item'] = [];
  const bodyText = content.body
    || [content.subjective, content.objective, content.assessment, content.plan]
        .filter(Boolean)
        .join('\n\n');

  if (template?.item?.length) {
    for (const item of template.item) {
      if (item.type === 'display') continue;
      const linkId = item.linkId;
      let answerText = '';
      if (['q1', 'body', 'content', 'subjective'].includes(linkId)) {
        answerText = content.subjective ?? content.body ?? '';
      } else if (['title', 'q0'].includes(linkId)) {
        answerText = content.title ?? '';
      } else if (['q2', 'objective', 'review'].includes(linkId)) {
        answerText = content.objective ?? '';
      } else if (['q3', 'assessment'].includes(linkId)) {
        answerText = content.assessment ?? '';
      } else if (['q4', 'plan'].includes(linkId)) {
        answerText = content.plan ?? '';
      } else {
        answerText = bodyText;
      }
      if (answerText) {
        items.push({
          linkId,
          answer: [{ valueString: answerText }],
        });
      }
    }
  } else {
    // No template resolved. Write labelled, SOAP-aligned items (rather than one merged blob) so the
    // title and section structure survive the reload — `extractNoteContentSections` falls back to
    // `item.text` when there is no Questionnaire to source labels from.
    const fallbackFields: { linkId: string; text: string; value?: string }[] = [
      { linkId: COMPOSED_NOTE_TITLE_LINK_ID, text: 'Title', value: content.title },
      { linkId: 'subjective', text: 'Subjective', value: content.subjective },
      { linkId: 'objective', text: 'Objective', value: content.objective },
      { linkId: 'assessment', text: 'Assessment', value: content.assessment },
      { linkId: 'plan', text: 'Plan', value: content.plan },
    ];
    for (const field of fallbackFields) {
      if (!field.value?.trim()) continue;
      items.push({
        linkId: field.linkId,
        text: field.text,
        answer: [{ valueString: field.value }],
      });
    }
    if (content.body?.trim()) {
      items.push({ linkId: 'body', text: 'Note', answer: [{ valueString: content.body }] });
    }
  }

  const extensions: QuestionnaireResponse['extension'] = [];
  if (episodeId) {
    extensions.push({
      url: EPISODE_OF_CARE_CONTEXT_EXTENSION,
      valueReference: { reference: `EpisodeOfCare/${episodeId}` },
    });
  }
  extensions.push({ url: 'https://unify.health/fhir/detailed-status', valueCode: 'draft' });

  return {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    // The composer is not template-driven, so the kind tag is what lets the chart bundle classify
    // this note on reload instead of defaulting it to a Progress Note.
    meta: { tag: [composedChartNoteTag(kind)] },
    ...(canonicalRef ? { questionnaire: canonicalRef } : {}),
    subject: { reference: `Patient/${clientId}` },
    authored: new Date().toISOString(),
    item: items,
    extension: extensions,
  };
}

export function MedicalRecordTab({
  client,
  appointments,
  notes,
  adminNotes = [],
  episodes = [],
  medications = [],
  dischargeSummaries = [],
  historicalRecords = [],
  initialEpisodeId,
  episodeInteraction,
  onRefetch,
}: MedicalRecordTabProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = usePermissions();
  const { superviseePractitionerIds } = useSupervisionScope();
  const mayCreateAdminNote = canCreateAdminNote(role);
  const mayAuthorClinicalNote = canAuthorClinicalNote(role);

  // Shared resolver for medical record note templates (settings-aware)
  const { templates: medRecordTemplates } = useResolvedTemplates({
    target: 'medical-record-note',
  });
  const { templates: chartDocumentTemplates } = useResolvedTemplates({
    target: 'chart-document',
  });
  const defaultTreatmentPlanTemplate = useMemo(
    () => resolveDefaultTreatmentPlanTemplate(chartDocumentTemplates),
    [chartDocumentTemplates],
  );

  const authorizedEpisodeIds = useMemo(
    () => episodeInteraction?.authorizedEpisodeIds ?? new Set<string>(),
    [episodeInteraction?.authorizedEpisodeIds],
  );

  const canInteractEpisodeRow = useCallback(
    (episodeId: string | undefined, patientLevel?: boolean) => {
      if (!episodeInteraction) return true;
      return isEpisodeScopedRowVisible(
        episodeId,
        authorizedEpisodeIds,
        episodeInteraction.patientEpisodeCount,
        episodeInteraction.viewScope,
        patientLevel,
      );
    },
    [episodeInteraction, authorizedEpisodeIds],
  );

  const getAppointmentAccess = useCallback(
    (apt: Appointment, linkedNote: ClinicalNote | undefined) => {
      if (!episodeInteraction) return 'full' as const;
      return getAppointmentChartAccess(
        apt.episodeId,
        Boolean(apt.hasNote || linkedNote),
        authorizedEpisodeIds,
        episodeInteraction.patientEpisodeCount,
        episodeInteraction.viewScope,
      );
    },
    [episodeInteraction, authorizedEpisodeIds],
  );

  const getAssessmentAccess = useCallback(
    (episodeId: string | undefined) => {
      if (!episodeInteraction) return 'full' as const;
      return getAssessmentChartAccess(
        episodeId,
        authorizedEpisodeIds,
        episodeInteraction.patientEpisodeCount,
        episodeInteraction.viewScope,
      );
    },
    [episodeInteraction, authorizedEpisodeIds],
  );

  const notifyInteractionBlocked = useCallback(() => {
    notifications.show({
      title: 'Limited access',
      message:
        'This activity belongs to another episode. You can see it listed here, but only that episode’s team can open full details.',
      color: 'gray',
    });
  }, []);

  const [localMedications, setLocalMedications] = useState<Medication[]>(medications);
  const [localNotes, setLocalNotes] = useState<ClinicalNote[]>(notes);
  const [localAdminNotes, setLocalAdminNotes] = useState<AdminNote[]>(adminNotes);

  // The chart bundle refetches in place (WebSocket + tab-visibility), so these must track the
  // props or the timeline keeps showing whatever was loaded at mount.
  useEffect(() => { setLocalMedications(medications); }, [medications]);
  useEffect(() => { setLocalNotes(notes); }, [notes]);
  useEffect(() => { setLocalAdminNotes(adminNotes); }, [adminNotes]);

  const treatmentPlanDocumentIds = useTreatmentPlanChartDocumentIds(client.id, episodes, localNotes);

  const [providerFilter, setProviderFilter] = useState<string[]>([]);
  const validInitialEpisodeId = useMemo(() => {
    if (!initialEpisodeId) return undefined;
    return episodes.some((e) => e.id === initialEpisodeId) ? initialEpisodeId : undefined;
  }, [initialEpisodeId, episodes]);

  const [episodeFilter, setEpisodeFilter] = useState<string[]>(() =>
    validInitialEpisodeId ? [validInitialEpisodeId] : []
  );

  useEffect(() => {
    if (validInitialEpisodeId) {
      setEpisodeFilter([validInitialEpisodeId]);
      return;
    }
    if (episodes.length > 1) {
      setEpisodeFilter((prev) => (prev.length === 0 ? episodes.map((e) => e.id) : prev));
    } else if (episodes.length === 1) {
      setEpisodeFilter([episodes[0].id]);
    }
  }, [validInitialEpisodeId, episodes]);
  const [attendanceFilter, setAttendanceFilter] = useState<string[]>([
    'Requested',
    'Scheduled',
    'Completed',
    'No-Show',
    'Client Canceled',
    'Clinician Canceled',
  ]);
  const [noteStatusFilter, setNoteStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [displayFilter, setDisplayFilter] = useState<string[]>(DEFAULT_DISPLAY_FILTER);
  const [dateRangeValue, setDateRangeValue] = useState<[Date | null, Date | null]>([null, null]);
  const [sortOrder, setSortOrder] = useState<'newest-first' | 'oldest-first'>('newest-first');
  const [medicalRecordPage, setMedicalRecordPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<string | null>('50');
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [showInactiveMedications, setShowInactiveMedications] = useState(false);

  const [addNoteOpened, { open: openAddNote, close: closeAddNote }] = useDisclosure(false);
  const [otherDocumentOpened, { open: openOtherDocument, close: closeOtherDocument }] = useDisclosure(false);
  type AddNoteKind = 'chart' | 'admin';
  const [addNoteKind, setAddNoteKind] = useState<AddNoteKind>('chart');
  const [addNoteFormKey, setAddNoteFormKey] = useState(0);

  const [addMedicationOpened, { open: openAddMedication, close: closeAddMedication }] = useDisclosure(false);
  const [doseSpotOpened, { open: openDoseSpotDisclosure, close: closeDoseSpotDisclosure }] = useDisclosure(false);
  const [doseSpotIntent, setDoseSpotIntent] = useState<DoseSpotEmbedIntent>('newRx');
  const [doseSpotMedication, setDoseSpotMedication] = useState<Medication | null>(null);
  const [editMedicationOpened, { open: openEditMedication, close: closeEditMedication }] = useDisclosure(false);
  const [deleteMedicationOpened, { open: openDeleteMedication, close: closeDeleteMedication }] = useDisclosure(false);

  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [showFinishedEpisodes, setShowFinishedEpisodes] = useState(false);

  const [selectedAdminNote, setSelectedAdminNote] = useState<AdminNote | null>(null);

  const [viewAdminNoteOpened, { open: openViewAdminNote, close: closeViewAdminNote }] = useDisclosure(false);
  const [deleteAdminNoteOpened, { open: openDeleteAdminNote, close: closeDeleteAdminNote }] = useDisclosure(false);
  const [contactProfileOpened, { open: openContactProfile, close: closeContactProfile }] = useDisclosure(false);
  const [contactProfileModalContact, setContactProfileModalContact] = useState<Contact | null>(null);
  const [editAdminNoteForm, setEditAdminNoteForm] = useState({ title: '', content: '' });

  const getDefaultMedForm = () => ({
    name: '',
    dosage: '',
    frequency: '',
    prescribedBy: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'Active' as Medication['status'],
    notes: '',
  });
  const [editMedForm, setEditMedForm] = useState(getDefaultMedForm());
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);

  const providerOptions = useMemo(() => {
    const unique = [...new Set(appointments.map((a) => a.provider))];
    return unique.map((p) => ({ value: p, label: p }));
  }, [appointments]);

  const episodeFilterOptions = useMemo(() =>
    episodes.map((ep) => {
      const start = ep.startDate ? new Date(ep.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
      const end = ep.endDate ? new Date(ep.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present';
      const dateRange = start ? `${start} – ${end}` : '';
      return { value: ep.id, label: dateRange ? `${ep.name}: ${dateRange}` : ep.name };
    }),
    [episodes]
  );

  const attendanceOptions = useMemo(
    () => [
      { value: 'Requested', label: 'Requested' },
      { value: 'Scheduled', label: 'Scheduled' },
      { value: 'Completed', label: 'Completed' },
      { value: 'No-Show', label: 'No-Show' },
      { value: 'Client Canceled', label: 'Client Canceled' },
      { value: 'Clinician Canceled', label: 'Clinician Canceled' },
    ],
    []
  );


  const noteStatusOptions = useMemo(
    () => [
      { value: PENDING_DOCUMENTATION_LABEL, label: PENDING_DOCUMENTATION_LABEL },
      { value: 'Pending Signature', label: 'Pending Signature' },
      { value: 'Pending Cosignature', label: 'Pending Cosignature' },
      { value: 'Signed', label: 'Signed' },
    ],
    []
  );

  const notesByAppointmentId = useMemo(() => {
    const map = new Map<string, ClinicalNote>();
    localNotes.forEach((note) => {
      if (note.appointmentId) map.set(note.appointmentId, note);
    });
    return map;
  }, [localNotes]);

  const showFutureAppointments = displayFilter.includes(MEDICAL_RECORD_DISPLAY_FILTER.futureAppointments);
  const showAssessments = displayFilter.includes(MEDICAL_RECORD_DISPLAY_FILTER.assessments);
  const showPsychotherapyNoteAppointments = displayFilter.includes(
    MEDICAL_RECORD_DISPLAY_FILTER.psychotherapyNotes,
  );
  const showHistorical = timelineShowsHistorical(displayFilter);
  const [psychotherapyAppointmentIds, setPsychotherapyAppointmentIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!showPsychotherapyNoteAppointments || !client.id) {
      return;
    }
    let cancelled = false;
    void searchPsychotherapyNotesForPatient(medplum, client.id).then((docs) => {
      if (cancelled) return;
      const ids = new Set<string>();
      for (const doc of docs) {
        const row = toPsychotherapyNoteRow(doc);
        if (!row?.appointmentId) continue;
        const nonEmpty = row.isPdf ? Boolean(row.pdfUrl) : row.text.trim().length > 0;
        if (nonEmpty) ids.add(row.appointmentId);
      }
      setPsychotherapyAppointmentIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [showPsychotherapyNoteAppointments, client.id, medplum]);

  const filteredAppointments = useMemo(() => {
    const filtered = appointments.filter((apt) => {
      if (!showFutureAppointments && isFutureAppointment(apt)) return false;
      if (providerFilter.length > 0 && !providerFilter.includes(apt.provider)) return false;
      if (episodeFilter.length === 0) return false;
      if (!apt.episodeId || !episodeFilter.includes(apt.episodeId)) return false;
      if (attendanceFilter.length === 0) return false;
      if (!attendanceFilter.includes(apt.status)) return false;
      if (noteStatusFilter.length > 0) {
        if (apt.status !== 'Completed') return false;
        const linkedNote = notesByAppointmentId.get(apt.id);
        const doc = deriveAppointmentDocumentationStatus(apt, linkedNote);
        const matchesAny = noteStatusFilter.some((ns) => {
          if (ns === PENDING_DOCUMENTATION_LABEL) {
            return doc === PENDING_DOCUMENTATION_LABEL;
          }
          if (ns === 'Pending Signature') {
            return doc === 'Pending Signature';
          }
          if (ns === 'Pending Cosignature') {
            return doc === 'Pending Cosignature' || linkedNote?.noteStatus === 'Pending co-signature';
          }
          if (ns === 'Signed') {
            return doc === 'Signed';
          }
          return false;
        });
        if (!matchesAny) return false;
      }
      return true;
    });
    return [...filtered].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [
    appointments,
    showFutureAppointments,
    providerFilter,
    episodeFilter,
    attendanceFilter,
    noteStatusFilter,
    notesByAppointmentId,
  ]);

  const notesById = useMemo(() => {
    const map = new Map<string, ClinicalNote>();
    localNotes.forEach((note) => map.set(note.id, note));
    return map;
  }, [localNotes]);

  const otherNotes = useMemo(() => localNotes.filter((n) => !n.appointmentId), [localNotes]);

  const episodeNamesForFilter = useMemo(() => {
    if (episodeFilter.length === 0) return new Set<string>();
    return new Set(episodes.filter((e) => episodeFilter.includes(e.id)).map((e) => e.name));
  }, [episodeFilter, episodes]);

  const filteredOtherNotes = useMemo(() => {
    if (episodeFilter.length === 0) return [];
    return otherNotes.filter(
      (n) => n.patientLevel || (n.episodeId && episodeFilter.includes(n.episodeId)),
    );
  }, [otherNotes, episodeFilter]);

  const adminNotesWithNoteId = useMemo(() => localAdminNotes.filter((a) => a.noteId), [localAdminNotes]);
  const filteredAdminNotesLinked = useMemo(() => {
    if (episodeFilter.length === 0) return [];
    return adminNotesWithNoteId.filter((a) => {
      const note = a.noteId ? notesById.get(a.noteId) : undefined;
      if (!note) return false;
      if (note.episodeId) return episodeFilter.includes(note.episodeId);
      return episodeNamesForFilter.has(note.serviceType);
    });
  }, [adminNotesWithNoteId, episodeFilter, notesById, episodeNamesForFilter]);

  const adminNotesForNote = useMemo(
    () => (note: ClinicalNote | null) =>
      note ? localAdminNotes.filter((a) => a.noteId === note.id) : [],
    [localAdminNotes]
  );


  const otherAdminNotes = useMemo(() => {
    const standalone = localAdminNotes.filter((a) => !a.noteId);
    if (episodeFilter.length === 0) return [];
    return standalone.filter((a) => !a.episodeId || episodeFilter.includes(a.episodeId));
  }, [localAdminNotes, episodeFilter]);

  const filteredDischargeSummaries = useMemo(() => {
    if (episodeFilter.length === 0) return [];
    return dischargeSummaries.filter((ds) => episodeFilter.includes(ds.episodeId));
  }, [dischargeSummaries, episodeFilter]);

  const filteredHistoricalRecords = useMemo(() => {
    if (episodeFilter.length === 0) return [];
    return historicalRecords.filter(
      (record) => record.episodeId && episodeFilter.includes(record.episodeId),
    );
  }, [historicalRecords, episodeFilter]);

  const timelineItems = useMemo((): TimelineItem[] => {
    const assessmentNotes = showAssessments
      ? filteredOtherNotes.filter((n) => n.noteType === 'Assessments')
      : [];
    const nonAssessmentNotes = filteredOtherNotes
      .filter((n) => n.noteType !== 'Assessments')
      .filter((n) => noteMatchesDocumentationStatusFilter(n, noteStatusFilter))
      .filter((n) => shouldShowWhenHistoricalFilter(showHistorical, Boolean(n.isHistorical)));
    const items: TimelineItem[] = [
      ...filteredAppointments.map((a) => ({ kind: 'appointment' as const, data: a })),
      ...nonAssessmentNotes.map((n) => ({ kind: 'clinical_note' as const, data: n })),
      ...assessmentNotes.map((n) => ({ kind: 'assessment' as const, data: n })),
      ...filteredAdminNotesLinked.map((a) => ({ kind: 'admin_note' as const, data: a })),
      ...otherAdminNotes.map((a) => ({ kind: 'admin_note' as const, data: a })),
      ...filteredDischargeSummaries.map((d) => ({ kind: 'discharge_summary' as const, data: d })),
      ...(showHistorical
        ? filteredHistoricalRecords.map((h) => ({ kind: 'historical_record' as const, data: h }))
        : []),
    ];
    const desc = sortOrder === 'newest-first';
    return items.sort((a, b) => {
      const dateA = getItemDate(a).getTime();
      const dateB = getItemDate(b).getTime();
      if (dateA !== dateB) {
        return desc ? dateB - dateA : dateA - dateB;
      }
      const timeA = getTimelineSortTime(a);
      const timeB = getTimelineSortTime(b);
      return timeB - timeA;
    });
  }, [
    filteredAppointments,
    filteredOtherNotes,
    filteredAdminNotesLinked,
    otherAdminNotes,
    filteredDischargeSummaries,
    filteredHistoricalRecords,
    noteStatusFilter,
    showAssessments,
    showHistorical,
    sortOrder,
  ]);

  const filteredByDateRange = useMemo(() => {
    const [startVal, endVal] = dateRangeValue;
    if (!startVal && !endVal) return timelineItems;
    const start = startVal ? new Date(startVal).getTime() : null;
    const end = endVal ? new Date(endVal).getTime() : null;
    if (start === null && end === null) return timelineItems;
    return timelineItems.filter((item) => {
      const t = getItemDate(item).getTime();
      if (start !== null && t < start) return false;
      if (end !== null && t > end) return false;
      return true;
    });
  }, [timelineItems, dateRangeValue]);

  const filteredByType = useMemo(() => {
    let items = filteredByDateRange;
    if (typeFilter.length > 0) {
      const allowedRecordTypes = getRecordTypesForCategories(typeFilter);
      items = items.filter((item) => {
        const recordType = getRecordTypeForItem(item, notesByAppointmentId);
        return recordType !== undefined && allowedRecordTypes.has(recordType);
      });
    }
    if (showPsychotherapyNoteAppointments) {
      items = items.filter(
        (item) => item.kind === 'appointment' && psychotherapyAppointmentIds.has(item.data.id),
      );
    }
    return items;
  }, [
    filteredByDateRange,
    typeFilter,
    notesByAppointmentId,
    showPsychotherapyNoteAppointments,
    psychotherapyAppointmentIds,
  ]);

  const itemsPerPage = useMemo(
    () => parseInt(rowsPerPage || '50', 10),
    [rowsPerPage]
  );
  const totalRecordPages = useMemo(
    () => Math.max(1, Math.ceil(filteredByType.length / itemsPerPage)),
    [filteredByType.length, itemsPerPage]
  );
  const safeRecordPage = useMemo(
    () => Math.min(Math.max(1, medicalRecordPage), totalRecordPages),
    [medicalRecordPage, totalRecordPages]
  );
  const paginatedRecordItems = useMemo(
    () =>
      filteredByType.slice(
        (safeRecordPage - 1) * itemsPerPage,
        safeRecordPage * itemsPerPage
      ),
    [filteredByType, safeRecordPage, itemsPerPage]
  );

  const timelineByYear = useMemo(() => {
    const map = new Map<number, TimelineItem[]>();
    for (const item of paginatedRecordItems) {
      const year = getItemDate(item).getFullYear();
      const list = map.get(year) ?? [];
      list.push(item);
      map.set(year, list);
    }
    const yearOrder =
      sortOrder === 'newest-first'
        ? (a: [number, TimelineItem[]], b: [number, TimelineItem[]]) => b[0] - a[0]
        : (a: [number, TimelineItem[]], b: [number, TimelineItem[]]) => a[0] - b[0];
    return Array.from(map.entries())
      .sort(yearOrder)
      .map(([year, items]) => ({ year, items }));
  }, [paginatedRecordItems, sortOrder]);

  // Collapse the expanded record when filtering, sorting or pagination removes it from view.
  useEffect(() => {
    if (!expandedRecordId) return;
    const stillVisible = paginatedRecordItems.some(
      (item) => getTimelineItemKey(item) === expandedRecordId,
    );
    if (!stillVisible) setExpandedRecordId(null);
  }, [paginatedRecordItems, expandedRecordId]);

  /**
   * The one place a chart record is opened. `viewMode` decides the destination; callers apply
   * their access check first and pass the payload the destination needs.
   */
  const openChartRecord = useCallback(
    (params: {
      presentation: ChartRecordPresentation;
      ids: ChartRecordTargetIds;
      navigateState?: Record<string, unknown>;
      note?: ClinicalNote;
      adminNote?: AdminNote;
    }) => {
      const target = resolveChartRecordTarget(params.presentation.viewMode, params.ids);
      if (target.kind === 'none') return;
      if (target.kind === 'navigate') {
        navigate(target.to, params.navigateState ? { state: params.navigateState } : undefined);
        return;
      }
      if (target.modal === 'admin-note' && params.adminNote) {
        setSelectedAdminNote(params.adminNote);
        setEditAdminNoteForm({
          title: params.adminNote.title ?? '',
          content: params.adminNote.content,
        });
        openViewAdminNote();
        return;
      }
      if (target.modal === 'legacy-note' && params.note) {
        setSelectedNote(params.note);
        openView();
      }
    },
    [navigate, openView, openViewAdminNote],
  );

  const handleCloseView = () => {
    closeView();
    setSelectedNote(null);
  };

  const activeEpisodes = useMemo(() => episodes.filter((e) => e.status === 'active'), [episodes]);
  const followUpAndFinishedEpisodes = useMemo(
    () => episodes.filter((e) => e.status === 'follow-up' || e.status === 'finished'),
    [episodes]
  );
  const activeMedications = useMemo(() => localMedications.filter((m) => m.status === 'Active'), [localMedications]);
  const inactiveMedications = useMemo(
    () => localMedications.filter((m) => m.status === 'Discontinued' || m.status === 'On Hold'),
    [localMedications]
  );

  const { contacts: liveContactRows } = useMedplumPatientContacts(client.id);
  const chartContactRows = useMemo(
    () => filterContactRowsToEpisodeRelated(liveContactRows),
    [liveContactRows],
  );

  const handleChartContactRowActivate = useCallback(
    (row: ClientOverviewContactRow, contact: Contact) => {
      if (row.clientChartId) {
        navigate(`/dashboard/clients/${row.clientChartId}`);
        return;
      }
      if (contactRowHasGuarantorRole(row) && row.guarantorBillingProfileClientId) {
        navigate(`/dashboard/clients/${row.guarantorBillingProfileClientId}/profile?tab=billing`);
        return;
      }
      setContactProfileModalContact(contact);
      openContactProfile();
    },
    [navigate, openContactProfile],
  );

  const hasRightColumn =
    episodes.length > 0
    || (FEATURE_VISIBILITY.chartMedications && localMedications.length > 0)
    || (FEATURE_VISIBILITY.chartContacts && chartContactRows.length > 0);

  const getEpisodeName = (episodeId: string | undefined): string => {
    if (!episodeId) return '—';
    const ep = episodes.find((e) => e.id === episodeId);
    return ep?.name ?? '—';
  };

  const handleOpenAddNote = (kind: AddNoteKind) => {
    if (kind === 'admin' && !mayCreateAdminNote) {
      notifications.show({
        title: 'Access denied',
        message: 'You do not have permission to create admin notes.',
        color: 'red',
      });
      return;
    }
    setAddNoteKind(kind);
    setAddNoteFormKey((k) => k + 1);
    openAddNote();
  };

  const handleSelectChartDocumentTemplate = (template: Questionnaire) => {
    if (!template.id) {
      notifications.show({ title: 'Invalid template', message: 'This template cannot be opened.', color: 'red' });
      return;
    }
    const params = new URLSearchParams({ templateId: template.id });
    if (episodeFilter.length === 1) {
      params.set('episodeId', episodeFilter[0]);
    }
    navigate(`/dashboard/clients/${client.id}/chart-document/new?${params.toString()}`);
  };

  useEffect(() => {
    if (searchParams.get('newAdminNote') !== '1') return;
    if (!mayCreateAdminNote) return;
    setAddNoteKind('admin');
    setAddNoteFormKey((k) => k + 1);
    openAddNote();
    const next = new URLSearchParams(searchParams);
    next.delete('newAdminNote');
    setSearchParams(next, { replace: true });
  }, [searchParams, mayCreateAdminNote, openAddNote, setSearchParams]);

  const providerName = client.primaryProvider ?? client.provider ?? 'Current user';
  const { practitioner: currentPractitioner } = useCurrentUserPractitioner();

  const canCreateTreatmentPlanForEpisode = useCallback(
    (episode: Episode) =>
      canAuthorClinicalDocumentationForEpisode(
        episode.careManagerPractitionerId,
        chartScopeFromRoleAndPractitioner(
          role,
          currentPractitioner?.id,
          role === 'supervisor' ? superviseePractitionerIds : undefined,
        ),
      ),
    [currentPractitioner?.id, role, superviseePractitionerIds],
  );

  /**
   * Persist a composed note, then refetch so the server row replaces the optimistic one. A failed
   * write used to be swallowed into the console, which left the note on screen until the next
   * reload dropped it — roll back and say so instead.
   */
  const persistComposedNote = useCallback(
    async (qr: QuestionnaireResponse, optimisticId: string, label: string): Promise<void> => {
      try {
        const accounts = await getPatientAccountRefs(medplum, client.id).catch(
          () => [] as Reference[],
        );
        await medplum.createResource(
          applyComposedNoteAuthorship(qr, {
            author: currentPractitioner
              ? practitionerPreferredReference(currentPractitioner)
              : undefined,
            accounts,
          }),
        );
        onRefetch?.();
      } catch (err: unknown) {
        setLocalNotes((prev) => prev.filter((n) => n.id !== optimisticId));
        setLocalAdminNotes((prev) => prev.filter((n) => n.id !== optimisticId));
        notifications.show({
          title: `${label} not saved`,
          message: formatMedplumClientError(err),
          color: 'red',
          autoClose: false,
        });
      }
    },
    [medplum, client.id, currentPractitioner, onRefetch],
  );

  const handleSaveChartNote = useCallback((data: { form: Record<string, string>; templateId: string; episodeId: string }) => {
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.split('T')[0];
    const template = getChartNoteTemplateById(data.templateId);
    const getVal = (id: string) => (data.form[id] ?? '').trim();
    const title = getVal('title') || 'Chart note';
    let subjective = '';
    let objective = '';
    let assessment = '';
    const plan = getVal('plan') ?? '';
    if (template) {
      if (data.templateId === 'family') {
        subjective = [getVal('membersPresent'), getVal('presentingConcerns')].filter(Boolean).join('\n\n');
        objective = getVal('dynamicsObserved');
        assessment = getVal('progressGoals');
      } else if (data.templateId === 'couples') {
        subjective = [getVal('attendance'), getVal('topicsDiscussed')].filter(Boolean).join('\n\n');
        objective = getVal('interactionPattern');
        assessment = getVal('relationshipProgress');
      } else if (data.templateId === 'iop-group') {
        subjective = [getVal('moduleTopic'), getVal('clientReport')].filter(Boolean).join('\n\n');
        objective = getVal('participation');
        assessment = getVal('riskLevel');
      } else if (data.templateId === 'group') {
        subjective = getVal('groupTopic');
        objective = getVal('processObservations');
        assessment = getVal('participationNotes');
      } else {
        subjective = getVal('context') || getVal('reason') || getVal('presentation') || getVal('content');
        objective = getVal('review') || getVal('summary') || getVal('intervention');
        assessment = getVal('assessment');
      }
    } else {
      subjective = getVal('content');
    }
    const noteType = template?.id && ['family', 'couples', 'iop-group', 'group'].includes(template.id)
      ? (template.label as ClinicalNote['noteType'])
      : 'Chart Note';
    const newNote: ClinicalNote = {
      id: `note-${makeId()}`,
      type: 'Chart Note',
      noteType,
      date: dateStr,
      dateOfService: dateStr,
      provider: providerName,
      providerType: 'Therapist',
      serviceType: 'Clinical Documentation',
      appointmentType: 'N/A',
      serviceRendered: template?.label ?? 'Chart Note',
      title,
      status: 'Draft',
      noteStatus: 'Draft',
      billingStatus: 'N/A',
      episodeId: data.episodeId,
      lastModified: nowIso,
      soapNote: {
        subjective: subjective || '',
        objective,
        assessment,
        plan,
      },
    };
    setLocalNotes((prev) => [newNote, ...prev]);
    closeAddNote();
    const qr = buildNoteQuestionnaireResponse({
      kind: 'chart',
      template: getMedicalRecordTemplate('chart', medRecordTemplates),
      clientId: client.id,
      episodeId: data.episodeId,
      content: { title, subjective, objective, assessment, plan },
    });
    void persistComposedNote(qr, newNote.id, 'Chart note');
  }, [client.id, providerName, closeAddNote, medRecordTemplates, persistComposedNote]);

  const handleAddAddendum = (noteId: string, content: string, reasonForChange?: string) => {
    const nowIso = new Date().toISOString();
    const author = client.primaryProvider ?? client.provider ?? 'Current user';
    setLocalNotes((prev) =>
      prev.map((n) => {
        if (n.id !== noteId) return n;
        const addendums = n.addendums ?? [];
        return {
          ...n,
          addendums: [
            ...addendums,
            {
              id: `addendum-${makeId()}`,
              createdAt: nowIso,
              createdBy: author,
              content,
              reasonForChange,
            },
          ],
        };
      })
    );
  };

  const handleUpdateNote = useCallback((noteId: string, soapNote: NonNullable<ClinicalNote['soapNote']>) => {
    const nowIso = new Date().toISOString();
    setLocalNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, soapNote, lastModified: nowIso } : n))
    );
  }, []);

  const handleSaveAdminNote = useCallback((data: { title: string; content: string; episodeId?: string }) => {
    const nowIso = new Date().toISOString();
    const newAdmin: AdminNote = {
      id: `admin-${makeId()}`,
      title: data.title,
      content: data.content,
      createdBy: providerName,
      createdAt: nowIso,
      updatedAt: nowIso,
      type: 'Admin',
      ...(data.episodeId ? { episodeId: data.episodeId } : {}),
    };
    setLocalAdminNotes((prev) => [newAdmin, ...prev]);
    closeAddNote();
    const qr = buildNoteQuestionnaireResponse({
      kind: 'admin',
      template: getMedicalRecordTemplate('admin', medRecordTemplates),
      clientId: client.id,
      episodeId: data.episodeId,
      content: { title: data.title, body: data.content },
    });
    void persistComposedNote(qr, newAdmin.id, 'Admin note');
  }, [client.id, providerName, closeAddNote, medRecordTemplates, persistComposedNote]);

  const handleLegacyAddMedication = useCallback((data: Omit<Medication, 'id'>) => {
    const med: Medication = { ...data, id: makeId() };
    setLocalMedications((prev) => [med, ...prev]);
  }, []);

  const closeDoseSpot = useCallback(() => {
    closeDoseSpotDisclosure();
    setDoseSpotMedication(null);
  }, [closeDoseSpotDisclosure]);

  const openDoseSpotNewRx = useCallback(() => {
    setDoseSpotIntent('newRx');
    setDoseSpotMedication(null);
    openDoseSpotDisclosure();
  }, [openDoseSpotDisclosure]);

  const openDoseSpotRefill = useCallback(
    (med: Medication) => {
      setDoseSpotIntent('refill');
      setDoseSpotMedication(med);
      openDoseSpotDisclosure();
    },
    [openDoseSpotDisclosure]
  );

  const openDoseSpotDiscontinue = useCallback(
    (med: Medication) => {
      setDoseSpotIntent('discontinue');
      setDoseSpotMedication(med);
      openDoseSpotDisclosure();
    },
    [openDoseSpotDisclosure]
  );

  const handleDoseSpotDemoComplete = useCallback(
    (payload: DoseSpotDemoCompletePayload) => {
      if (payload.intent === 'refill') {
        const stamp = `Refill requested (demo) — ${new Date().toLocaleDateString()}`;
        setLocalMedications((prev) =>
          prev.map((m) =>
            m.id === payload.medicationId
              ? { ...m, notes: [m.notes, stamp].filter(Boolean).join('\n') }
              : m
          )
        );
        notifications.show({
          title: 'Refill',
          message: 'Refill request recorded locally (demo).',
          color: 'green',
        });
        closeDoseSpot();
        return;
      }
      const noteAppend = payload.note?.trim();
      const endIso = new Date().toISOString().split('T')[0];
      setLocalMedications((prev) =>
        prev.map((m) => {
          if (m.id !== payload.medicationId) return m;
          return {
            ...m,
            status: payload.status,
            endDate: payload.status === 'Discontinued' ? endIso : m.endDate,
            notes: noteAppend ? [m.notes, noteAppend].filter(Boolean).join('\n') : m.notes,
          };
        })
      );
      notifications.show({
        title: 'Medication updated',
        message: `Marked ${payload.status} locally (demo).`,
        color: 'teal',
      });
      closeDoseSpot();
    },
    [closeDoseSpot]
  );

  const handleEditMedicationOpen = (med: Medication) => {
    setSelectedMedication(med);
    setEditMedForm({
      name: med.name,
      dosage: med.dosage,
      frequency: med.frequency,
      prescribedBy: med.prescribedBy,
      startDate: med.startDate,
      endDate: med.endDate || '',
      status: med.status,
      notes: med.notes || '',
    });
    openEditMedication();
  };

  const handleSaveEditMedication = () => {
    if (!selectedMedication || !editMedForm.name.trim()) return;
    setLocalMedications((prev) =>
      prev.map((m) =>
        m.id === selectedMedication.id
          ? {
            ...m,
            name: editMedForm.name.trim(),
            dosage: editMedForm.dosage.trim(),
            frequency: editMedForm.frequency.trim(),
            prescribedBy: editMedForm.prescribedBy.trim(),
            startDate: editMedForm.startDate,
            endDate: editMedForm.endDate || undefined,
            status: editMedForm.status,
            notes: editMedForm.notes.trim() || undefined,
          }
          : m
      )
    );
    setSelectedMedication(null);
    closeEditMedication();
  };

  const handleDeleteMedication = (med: Medication) => {
    setSelectedMedication(med);
    openDeleteMedication();
  };

  const confirmDeleteMedication = () => {
    if (selectedMedication) {
      setLocalMedications((prev) => prev.filter((m) => m.id !== selectedMedication.id));
      setSelectedMedication(null);
    }
    closeDeleteMedication();
  };

  const handleViewAdminNote = (adminNote: AdminNote) => {
    if (!canInteractEpisodeRow(adminNote.episodeId)) {
      notifyInteractionBlocked();
      return;
    }
    setSelectedAdminNote(adminNote);
    setEditAdminNoteForm({ title: adminNote.title ?? '', content: adminNote.content });
    openViewAdminNote();
  };

  const handleEditAdminNoteOpen = (adminNote: AdminNote) => {
    handleViewAdminNote(adminNote);
  };

  const handleSaveEditAdminNote = () => {
    if (!selectedAdminNote) return;
    const nowIso = new Date().toISOString();
    setLocalAdminNotes((prev) =>
      prev.map((a) =>
        a.id === selectedAdminNote.id
          ? { ...a, ...editAdminNoteForm, type: 'Admin' as const, updatedAt: nowIso }
          : a
      )
    );
    setSelectedAdminNote(null);
    closeViewAdminNote();
  };

  const handleDeleteAdminNoteOpen = (adminNote: AdminNote) => {
    setSelectedAdminNote(adminNote);
    openDeleteAdminNote();
  };

  const confirmDeleteAdminNote = () => {
    if (selectedAdminNote) {
      setLocalAdminNotes((prev) => prev.filter((a) => a.id !== selectedAdminNote.id));
      setSelectedAdminNote(null);
    }
    closeDeleteAdminNote();
  };

  // ─── Filter row ───────────────────────────────────────────────────────
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const filterDrawerSections = useMemo(() => [
    {
      label: 'Provider',
      options: providerOptions.map((o) => ({ value: o.value, label: o.label })),
    },
    {
      label: 'Attendance',
      options: attendanceOptions.map((o) => ({ value: o.value, label: o.label })),
    },
    {
      label: 'Documentation Status',
      options: noteStatusOptions.map((o) => ({ value: o.value, label: o.label })),
    },
    {
      label: 'Record Type',
      options: MEDICAL_RECORD_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    },
    {
      label: 'Include in timeline',
      options: [
        { value: MEDICAL_RECORD_DISPLAY_FILTER.futureAppointments, label: 'Future appointments' },
        { value: MEDICAL_RECORD_DISPLAY_FILTER.assessments, label: 'Assessments' },
        // Psychotherapy notes are care-team only; staff never see that they exist.
        ...(mayAuthorClinicalNote
          ? [{ value: MEDICAL_RECORD_DISPLAY_FILTER.psychotherapyNotes, label: 'Psychotherapy Note' }]
          : []),
        { value: MEDICAL_RECORD_DISPLAY_FILTER.historical, label: 'Historical' },
      ],
    },
  ], [providerOptions, attendanceOptions, noteStatusOptions, mayAuthorClinicalNote]);

  const filterDrawerValue = useMemo(() => ({
    'Provider': providerFilter,
    'Attendance': attendanceFilter,
    'Documentation Status': noteStatusFilter,
    'Record Type': typeFilter,
    'Include in timeline': displayFilter,
  }), [providerFilter, attendanceFilter, noteStatusFilter, typeFilter, displayFilter]);

  const handleFilterDrawerChange = (sectionLabel: string, selected: string[]) => {
    if (sectionLabel === 'Provider') setProviderFilter(selected);
    else if (sectionLabel === 'Attendance') setAttendanceFilter(selected);
    else if (sectionLabel === 'Documentation Status') setNoteStatusFilter(selected);
    else if (sectionLabel === 'Record Type') setTypeFilter(selected);
    else if (sectionLabel === 'Include in timeline') setDisplayFilter(selected);
  };

  const handleFilterDrawerClear = () => {
    setProviderFilter([]);
    setAttendanceFilter([]);
    setNoteStatusFilter([]);
    setTypeFilter([]);
    setDisplayFilter(DEFAULT_DISPLAY_FILTER);
  };

  const filterRow = (
    <Group gap="sm" wrap="wrap" align="flex-end" w="100%">
      <Box style={{ flex: 1, minWidth: 160 }}>
        <DateRangeSelect
          value={dateRangeValue}
          onChange={setDateRangeValue}
          placeholder="Date range"
          size="md"
          w="100%"
        />
      </Box>
      <Box style={{ flex: 1, minWidth: 160 }}>
        <FilterMultiSelect
          allLabel="All Episodes of Care"
          data={episodeFilterOptions}
          value={episodeFilter}
          onChange={setEpisodeFilter}
          w="100%"
        />
      </Box>
      <Button
        variant="subtle"
        onClick={() => setFilterDrawerOpen(true)}
      >
        More filters
      </Button>
    </Group>
  );

  const addNoteMenu = (
    <Menu shadow="md" width={240} position="bottom-end">
      <Menu.Target>
        <Button leftSection={<IconPlus size={16} />} variant="light" size="sm">
          New
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {mayAuthorClinicalNote && (
          <Menu.Item leftSection={<IconNote size={14} />} onClick={() => handleOpenAddNote('chart')}>
            Chart note
          </Menu.Item>
        )}
        {mayCreateAdminNote && (
          <Menu.Item leftSection={<IconNote size={14} />} onClick={() => handleOpenAddNote('admin')}>
            Admin note
          </Menu.Item>
        )}
        <Menu.Item leftSection={<IconFileText size={14} />} onClick={openOtherDocument}>
          Add other document
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  // ─── Show Note / Show less helpers ──────────────────────────────────────
  const showNoteLink = (recordKey: string) => (
    <Anchor
      component="button"
      type="button"
      size="sm"
      fw={500}
      mt={4}
      onClick={(e) => {
        e.stopPropagation();
        setExpandedRecordId(recordKey);
      }}
      style={{ alignSelf: 'flex-start' }}
    >
      Show Note
    </Anchor>
  );

  const showLessLink = (
    <Anchor
      component="button"
      type="button"
      size="sm"
      fw={500}
      mt={4}
      onClick={(e) => {
        e.stopPropagation();
        setExpandedRecordId(null);
      }}
      style={{ alignSelf: 'flex-start' }}
    >
      Show less
    </Anchor>
  );

  const renderChartHeader = (year?: number, showControls = true) => (
    <Box py="xs" mb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
      <Group justify={year != null ? 'space-between' : 'flex-end'} align="center" w="100%">
        {year != null ? (
          <Text size="lg" fw={600} c="gray.7">
            {year}
          </Text>
        ) : null}
        {showControls ? (
          <Group gap="sm">
            <SegmentedControl
              value={sortOrder}
              onChange={(v) => {
                setSortOrder(v as 'newest-first' | 'oldest-first');
                setMedicalRecordPage(1);
              }}
              data={[
                { value: 'newest-first', label: 'Newest' },
                { value: 'oldest-first', label: 'Oldest' },
              ]}
              size="sm"
              aria-label="Sort order"
            />
            {addNoteMenu}
          </Group>
        ) : null}
      </Group>
    </Box>
  );

  // ─── Timeline cards ───────────────────────────────────────────────────
  const medicalRecordPaper = (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        {filteredByType.length === 0 ? (
          <>
            {renderChartHeader()}
            <Text c="gray.6" ta="center" py="xl">
              No chart entries
            </Text>
          </>
        ) : (
          <Stack gap={0}>
            {timelineByYear.map(({ year, items }, yearIndex) => (
              <Box key={year}>
                {renderChartHeader(year, yearIndex === 0)}
                <Stack gap="sm">
                  {items.map((item) => {
                    const itemDate = getItemDate(item);
                    const recordKey = getTimelineItemKey(item);
                    const isExpanded = expandedRecordId === recordKey;

                    if (item.kind === 'appointment') {
                      const apt = item.data;
                      const linkedNote = notesByAppointmentId.get(apt.id);
                      const apptAccess = getAppointmentAccess(apt, linkedNote);
                      const allowFullDrillIn = apptAccess === 'full';
                      const mockNoteEntry = apt.hasNote ? getMockNoteEntryForAppointment(apt.id) : null;
                      // No linked note yet (e.g. structured QR only) — treat as session documentation.
                      const linkedNoteIsSessionDoc = linkedNote
                        ? isSessionDocumentationRecordType(linkedNote.noteType)
                        : true;
                      const presentation = resolveChartRecordPresentation({
                        source: 'appointment',
                        recordType: getRecordTypeForItem(item, notesByAppointmentId),
                        hasLinkedSessionNote: apt.hasNote && linkedNoteIsSessionDoc,
                      });
                      const canExpand =
                        presentation.canExpandInline &&
                        (Boolean(mockNoteEntry) || hasRenderableSessionNoteContent(linkedNote));
                      const isTelehealth = apt.location.type === 'Telehealth';
                      const locationLabel = isTelehealth ? (apt.location.platform || 'Telehealth') : 'In Person';
                      const timeDisplay = linkedNote
                        ? formatTimeOnly(linkedNote.lastModified ?? linkedNote.signedDate)
                        : formatRecordTime(apt.date, apt.time);
                      const fullDate = formatFullDate(itemDate);
                      return (
                        <ChartRecordCard
                          key={recordKey}
                          presentation={presentation}
                          ariaLabel={`${apt.serviceType} — ${fullDate}`}
                          onActivate={() => {
                            const episodeDisplayName = getEpisodeName(apt.episodeId);
                            const mockState = buildMockAppointmentPageState(client, apt, appointments, {
                              episodeDisplayName: episodeDisplayName !== '—' ? episodeDisplayName : undefined,
                              appointmentOnly: apptAccess === 'appointment-only',
                            });
                            openChartRecord({
                              presentation,
                              ids: { clientId: client.id, appointmentId: apt.id },
                              navigateState: { [MOCK_STATE_KEY]: mockState },
                            });
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                              <Text size="sm" fw={600} truncate>
                                {apt.serviceType}
                              </Text>
                              {apt.status === 'Completed' && (() => {
                                const ln = notesByAppointmentId.get(apt.id);
                                const doc = deriveAppointmentDocumentationStatus(apt, ln);
                                if (doc === 'Pending Cosignature' || ln?.noteStatus === 'Pending co-signature') {
                                  return <Badge color="orange" variant="light" size="sm">Pending Cosignature</Badge>;
                                }
                                if (doc === 'Signed' || ln?.noteStatus === 'Signed') {
                                  return <Badge color="blue" variant="light" size="sm">Signed</Badge>;
                                }
                                if (doc === 'Pending Signature') {
                                  return <Badge color="yellow" variant="light" size="sm">Pending Signature</Badge>;
                                }
                                if (doc === PENDING_DOCUMENTATION_LABEL) {
                                  return (
                                    <Badge
                                      color="orange"
                                      variant="filled"
                                      size="md"
                                      leftSection={<IconAlertCircle size={14} />}
                                      styles={{
                                        root: {
                                          boxShadow:
                                            '0 0 0 2px var(--mantine-color-orange-3), 0 2px 8px rgba(234, 88, 12, 0.35)',
                                          fontWeight: 600,
                                        },
                                      }}
                                    >
                                      {PENDING_DOCUMENTATION_LABEL}
                                    </Badge>
                                  );
                                }
                                return null;
                              })()}
                            </Group>
                          </Group>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                            <Text size="xs" c="gray.5">
                              {fullDate}
                            </Text>
                            {timeDisplay && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.5">{timeDisplay}</Text>
                              </>
                            )}
                            <Text size="xs" c="gray.4">•</Text>
                            <Text size="xs" c="gray.6">{apt.provider}</Text>
                            <Text size="xs" c="gray.4">•</Text>
                            {isTelehealth ? (
                              <IconVideo size={13} style={{ color: 'var(--mantine-color-blue-5)', flexShrink: 0 }} />
                            ) : (
                              <IconMapPin size={13} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
                            )}
                            <Text size="xs" c="gray.6">
                              {locationLabel}
                            </Text>
                          </Group>
                          {apt.status === 'Completed' &&
                            (() => {
                              const doc = deriveAppointmentDocumentationStatus(apt, linkedNote);
                              if (doc == null) return null;
                              return (
                                <Text size="xs" c="gray.6" mt={4} fw={500}>
                                  Documentation: {doc}
                                </Text>
                              );
                            })()}
                          {apt.notes && (
                            <Text size="sm" c="gray.7" mt="xs">
                              {apt.notes}
                            </Text>
                          )}
                          {apt.hasNote && !allowFullDrillIn && (
                            <Text size="xs" c="dimmed" mt="sm">
                              No note visible on this chart view for this session.
                            </Text>
                          )}
                          {canExpand && allowFullDrillIn && (
                            <Box>
                              {isExpanded ? (
                                <Stack gap="sm" mt="sm">
                                  <ScrollArea.Autosize mah={480} type="scroll" offsetScrollbars>
                                    <Box pr="xs">
                                      {mockNoteEntry ? (
                                        <QRDocumentWithComments
                                          questionnaireResponse={mockNoteEntry.response}
                                          questionnaire={mockNoteEntry.questionnaire}
                                          comments={[]}
                                          onAddComment={() => { }}
                                          isSigned={mockNoteEntry.response.status === 'completed'}
                                          readOnly
                                      hideTitle
                                    />
                                  ) : (
                                    <NoteBodySections note={linkedNote} />
                                  )}
                                    </Box>
                                  </ScrollArea.Autosize>
                                  {showLessLink}
                                </Stack>
                              ) : (
                                showNoteLink(recordKey)
                              )}
                            </Box>
                          )}
                        </ChartRecordCard>
                      );
                    }

                    if (item.kind === 'clinical_note') {
                      const note = item.data;
                      const presentation = resolveChartRecordPresentation({
                        source: 'clinical-note',
                        recordType: getRecordTypeForItem(item, notesByAppointmentId),
                        chartDocumentResponseId: note.chartDocumentResponseId,
                      });
                      const allowDrillIn =
                        normalizeMedicalRecordType(presentation.recordType) === 'Chart Notes' ||
                        canInteractEpisodeRow(note.episodeId, note.patientLevel);
                      const isStandaloneStructured = ['Chart Note', 'Chart Notes', 'Risk Assessment', 'Safety Plan'].includes(note.noteType);
                      const isPrivatePsychotherapy = (note.noteType === 'Psychotherapy Note' || note.noteType === 'Psychotherapy Notes') && note.noteStatus === 'Private';
                      // Chart documents keep their content in the QuestionnaireResponse; their SOAP
                      // fields are signature placeholders and must never be shown as a preview.
                      const description = isStandaloneStructured || isPrivatePsychotherapy || note.chartDocumentResponseId
                        ? ''
                        : (note.soapNote?.subjective || note.title || `${note.noteType} — ${note.serviceRendered || note.serviceType}`);
                      const canExpand = presentation.canExpandInline && hasRenderableSessionNoteContent(note);
                      const noteTime = formatTimeOnly(note.lastModified ?? note.signedDate);
                      const fullDate = formatFullDate(itemDate);
                      const activateNote = !allowDrillIn
                        ? notifyInteractionBlocked
                        : presentation.viewMode === 'none'
                          ? undefined
                          : () =>
                            openChartRecord({
                              presentation,
                              ids: {
                                clientId: client.id,
                                chartDocumentResponseId: note.chartDocumentResponseId,
                              },
                              note,
                            });
                      return (
                        <ChartRecordCard
                          key={recordKey}
                          presentation={presentation}
                          ariaLabel={`${note.title} — ${fullDate}`}
                          restricted={!allowDrillIn}
                          onActivate={activateNote}
                        >
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} truncate>
                              {note.title}
                            </Text>
                            {note.isHistorical ? (
                              <Badge color="gray" variant="light" size="sm">Historical</Badge>
                            ) : (
                              getNoteStatusBadge(note.noteStatus)
                            )}
                          </Group>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                            <Text size="xs" c="gray.5">
                              {fullDate}
                            </Text>
                            {noteTime && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.5">{noteTime}</Text>
                              </>
                            )}
                            <Text size="xs" c="gray.4">•</Text>
                            <Text size="xs" c="gray.6">{note.provider}</Text>
                            {(note.noteType === 'Chart Note' || note.noteType === 'Chart Notes') && note.episodeId && getEpisodeName(note.episodeId) !== '—' && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.6">{getEpisodeName(note.episodeId)}</Text>
                              </>
                            )}
                          </Group>
                          {description ? (
                            <Text size="sm" c="gray.7" mt="xs">
                              {description}
                            </Text>
                          ) : null}
                          {canExpand && (
                            <Box>
                              {!allowDrillIn ? (
                                <Text size="xs" c="dimmed" mt="sm">
                                  Full note content is available to this episode’s care team.
                                </Text>
                              ) : isExpanded ? (
                                <Stack gap="sm" mt="sm">
                                  <ScrollArea.Autosize mah={480} type="scroll" offsetScrollbars>
                                    <Box pr="xs">
                                      <NoteBodySections note={note} />
                                    </Box>
                                  </ScrollArea.Autosize>
                                  {showLessLink}
                                </Stack>
                              ) : (
                                showNoteLink(recordKey)
                              )}
                            </Box>
                          )}
                        </ChartRecordCard>
                      );
                    }

                    if (item.kind === 'discharge_summary') {
                      const ds = item.data;
                      const allowDrillIn = canInteractEpisodeRow(ds.episodeId);
                      const episode = episodes.find((e) => e.id === ds.episodeId);
                      const previewText = ds.reasonForDischarge || ds.summaryOfCare || '';
                      const previewTruncated = previewText.length > 140 ? `${previewText.slice(0, 137)}...` : previewText;
                      const fullDate = formatFullDate(itemDate);
                      const dsTime = formatTimeOnly(ds.signedAt);
                      const presentation = resolveChartRecordPresentation({
                        source: 'discharge-summary',
                        recordType: getRecordTypeForItem(item, notesByAppointmentId),
                      });
                      return (
                        <ChartRecordCard
                          key={recordKey}
                          presentation={presentation}
                          ariaLabel={`Discharge summary — ${fullDate}`}
                          restricted={!allowDrillIn}
                          onActivate={
                            allowDrillIn
                              ? () =>
                                openChartRecord({
                                  presentation,
                                  ids: { clientId: client.id, dischargeSummaryId: ds.id },
                                })
                              : notifyInteractionBlocked
                          }
                        >
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} truncate>
                              Discharge summary — {episode?.name ?? 'Episode'}
                            </Text>
                            <Badge color="green" variant="light" size="sm">Signed</Badge>
                          </Group>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                            <Text size="xs" c="gray.5">
                              {fullDate}
                            </Text>
                            {dsTime && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.5">{dsTime}</Text>
                              </>
                            )}
                            {ds.signedBy && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.6">{ds.signedBy}</Text>
                              </>
                            )}
                          </Group>
                          {previewTruncated && (
                            <Text size="sm" c="gray.7" mt="xs">
                              {previewTruncated}
                            </Text>
                          )}
                        </ChartRecordCard>
                      );
                    }

                    if (item.kind === 'assessment') {
                      const assessNote = item.data;
                      const assessAccess = getAssessmentAccess(assessNote.episodeId);
                      const assessTime = formatTimeOnly(assessNote.lastModified ?? assessNote.signedDate);
                      const assessFullDate = formatFullDate(itemDate);
                      const assessTitle = assessNote.title;
                      const scoreLine = assessNote.soapNote?.objective?.trim() ?? '';
                      const presentation = resolveChartRecordPresentation({
                        source: 'assessment',
                        recordType: getRecordTypeForItem(item, notesByAppointmentId),
                      });
                      const assessmentIds: ChartRecordTargetIds = {
                        clientId: client.id,
                        ...(assessNote.assessmentTaskId ? { assessmentTaskId: assessNote.assessmentTaskId } : {}),
                        ...(assessNote.assessmentId ? { assessmentId: assessNote.assessmentId } : {}),
                      };
                      const canOpenAssessment = Boolean(
                        assessNote.assessmentTaskId || assessNote.assessmentId,
                      );
                      return (
                        <ChartRecordCard
                          key={recordKey}
                          presentation={presentation}
                          ariaLabel={`${assessTitle} — ${assessFullDate}`}
                          onActivate={
                            canOpenAssessment
                              ? () =>
                                openChartRecord({
                                  presentation,
                                  ids: assessmentIds,
                                  ...(assessNote.assessmentTaskId
                                    ? {
                                      navigateState: {
                                        mode: 'view',
                                        assessmentOnly: assessAccess === 'assessment-only',
                                      },
                                    }
                                    : {}),
                                })
                              : undefined
                          }
                        >
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} truncate>
                              {assessTitle}
                            </Text>
                          </Group>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                            <Text size="xs" c="gray.5">{assessFullDate}</Text>
                            {assessTime && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.5">{assessTime}</Text>
                              </>
                            )}
                            <Text size="xs" c="gray.4">•</Text>
                            <Text size="xs" c="gray.6">{assessNote.provider}</Text>
                            {scoreLine && (
                              <>
                                <Text size="xs" c="gray.4">•</Text>
                                <Text size="xs" c="gray.6">{scoreLine}</Text>
                              </>
                            )}
                          </Group>
                        </ChartRecordCard>
                      );
                    }

                    if (item.kind === 'historical_record') {
                      const historical = item.data;
                      const allowDrillIn = canInteractEpisodeRow(historical.episodeId);
                      const fullDate = formatFullDate(itemDate);
                      const presentation = resolveChartRecordPresentation({
                        source: 'historical-record',
                        recordType: getRecordTypeForItem(item, notesByAppointmentId),
                      });
                      const openPdf = historical.id && historical.attachment
                        ? () => {
                          void (async () => {
                            try {
                              const pdf = await downloadDocumentAttachment(medplum, historical.id);
                              await openHistoricalPdfInNewTab(
                                medplum,
                                `data:${pdf.contentType};base64,${pdf.data}`,
                              );
                            } catch (err) {
                              notifications.show({
                                title: 'Could not open PDF',
                                message: err instanceof Error ? err.message : 'Failed to load historical PDF.',
                                color: 'red',
                              });
                            }
                          })();
                        }
                        : undefined;
                      return (
                        <ChartRecordCard
                          key={recordKey}
                          presentation={presentation}
                          ariaLabel={`${historical.title} — ${fullDate}`}
                          restricted={!allowDrillIn}
                          onActivate={
                            openPdf
                              ? openPdf
                              : allowDrillIn
                                ? undefined
                                : notifyInteractionBlocked
                          }
                        >
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} truncate>
                              {historical.title}
                            </Text>
                            <Badge color="gray" variant="light" size="sm">Historical</Badge>
                          </Group>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                            <Text size="xs" c="gray.5">{fullDate}</Text>
                            <Text size="xs" c="gray.4">•</Text>
                            <Text size="xs" c="gray.6">{historical.provider}</Text>
                            <Text size="xs" c="gray.4">•</Text>
                            <Text size="xs" c="gray.6">
                              {getRecordTypeForItem(item, notesByAppointmentId)}
                            </Text>
                          </Group>
                        </ChartRecordCard>
                      );
                    }

                    // Admin note
                    const adminNote = item.data as AdminNote;
                    const allowDrillIn = canInteractEpisodeRow(adminNote.episodeId);
                    const adminTime = formatTimeOnly(adminNote.updatedAt ?? adminNote.createdAt);
                    const adminFullDate = formatFullDate(itemDate);
                    const adminPresentation = resolveChartRecordPresentation({
                      source: 'admin-note',
                      recordType: getRecordTypeForItem(item, notesByAppointmentId),
                    });
                    return (
                      <ChartRecordCard
                        key={recordKey}
                        presentation={adminPresentation}
                        ariaLabel={`${adminNote.title ?? adminNote.type} — ${adminFullDate}`}
                        restricted={!allowDrillIn}
                        onActivate={
                          allowDrillIn
                            ? () =>
                              openChartRecord({
                                presentation: adminPresentation,
                                ids: { clientId: client.id },
                                adminNote,
                              })
                            : notifyInteractionBlocked
                        }
                      >
                        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                          <Text size="sm" fw={600}>
                            {adminNote.title ?? adminNote.type}
                          </Text>
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap" mt={2}>
                          <Text size="xs" c="gray.5">
                            {adminFullDate}
                          </Text>
                          {adminTime && (
                            <>
                              <Text size="xs" c="gray.4">•</Text>
                              <Text size="xs" c="gray.5">{adminTime}</Text>
                            </>
                          )}
                          <Text size="xs" c="gray.4">•</Text>
                          <Text size="xs" c="gray.6">{adminNote.createdBy}</Text>
                        </Group>
                        <Text size="sm" c="gray.7" mt="xs" lineClamp={2}>
                          {adminNote.content}
                        </Text>
                      </ChartRecordCard>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        <Group justify="space-between" mt="md" wrap="wrap">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Rows per page:
            </Text>
            <Select
              value={rowsPerPage}
              onChange={(value) => {
                setRowsPerPage(value);
                setMedicalRecordPage(1);
              }}
              data={[...ROWS_PER_PAGE_OPTIONS]}
              w={80}
              size="xs"
            />
            <Text size="sm" c="dimmed">
              {filteredByType.length === 0
                ? '0 of 0'
                : `${(safeRecordPage - 1) * itemsPerPage + 1}-${Math.min(safeRecordPage * itemsPerPage, filteredByType.length)} of ${filteredByType.length}`}
              {filteredAppointments.length !== appointments.length && (
                <> ({filteredAppointments.length} appointments match filters)</>
              )}
            </Text>
          </Group>
          <Pagination
            total={totalRecordPages}
            value={safeRecordPage}
            onChange={setMedicalRecordPage}
            aria-label="Chart list pagination"
          />
        </Group>
      </Stack>
    </Paper>
  );

  // ─── Episode card sub-components ─────────────────────────────────────
  const EpisodePartyRow = ({
    icon,
    label,
    party,
    href,
  }: {
    icon: React.ReactNode;
    label: string;
    party: EpisodeParty;
    href?: string;
  }) => (
    <Group gap="xs" mt={3} wrap="nowrap">
      <Box style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }}>{icon}</Box>
      <Text size="xs" c="gray.6" style={{ lineHeight: 1.4 }}>
        <Text span fw={500} c="gray.7">{label}:</Text>{' '}
        {href ? (
          <Anchor component={Link} to={href} size="xs" fw={500} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {party.name}
          </Anchor>
        ) : (
          party.name
        )}
        {party.relationship && party.relationship !== 'Self' && (
          <Text span c="gray.5"> ({party.relationship})</Text>
        )}
      </Text>
    </Group>
  );

  // ─── Episodes of Care panel ───────────────────────────────────────────
  const episodesPanel = (
    <Stack gap={0}>
      <Title order={4} mb="lg">Episodes of Care</Title>
      {activeEpisodes.length > 0 && (
        <Stack gap="sm">
          {activeEpisodes.map((episode) => {
            const isCurrentChartPrimary = episode.primaryClinicalPatient?.id === client.id;
            const primaryClientId = episode.primaryClinicalPatient?.id ?? client.id;
            return (
              <Paper
                key={episode.id}
                withBorder
                p="md"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
                onClick={() => {
                  navigate(`/dashboard/clients/${client.id}/episode-of-care/${episode.id}`);
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Group gap="xs" mb={2} wrap="nowrap">
                    <Text fw={500} style={{ flexShrink: 0 }}>
                      {episode.name}
                    </Text>
                    <Badge color={isCurrentChartPrimary ? 'blue' : 'gray'} variant="light" size="sm">
                      {isCurrentChartPrimary ? 'Primary' : 'Supporting'}
                    </Badge>
                    {episode.isOverdue && (
                      <Badge color="orange" variant="light" size="sm">
                        Action Required
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" c="gray.6">
                    Primary Provider: {episode.provider}
                  </Text>
                  <Text size="xs" c="gray.5">
                    {formatDate(episode.startDate)} – {episode.endDate ? formatDate(episode.endDate) : 'Present'}
                  </Text>
                  <Stack gap={0} mt={4}>
                    {episode.primaryClinicalPatient && (
                      <EpisodePartyRow
                        icon={<IconUsers size={13} />}
                        label="Primary Client"
                        party={episode.primaryClinicalPatient}
                        href={`/dashboard/clients/${episode.primaryClinicalPatient.id}`}
                      />
                    )}
                    {episode.groupMembers && episode.groupMembers.filter((m) => m.id !== episode.primaryClinicalPatient?.id).length > 0 && (
                      <Group gap="xs" mt={3} wrap="nowrap">
                        <Box style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }}><IconUsers size={13} /></Box>
                        <Text size="xs" c="gray.6" style={{ lineHeight: 1.4 }}>
                          <Text span fw={500} c="gray.7">Other Clients:</Text>{' '}
                          {episode.groupMembers
                            .filter((m) => m.id !== episode.primaryClinicalPatient?.id)
                            .map((m) => m.name)
                            .join(', ')}
                        </Text>
                      </Group>
                    )}
                    {episode.guarantor && (
                      <EpisodePartyRow
                        icon={<IconShield size={13} />}
                        label="Guarantor"
                        party={episode.guarantor}
                        href={`/dashboard/clients/${primaryClientId}/profile?tab=billing`}
                      />
                    )}
                  </Stack>
                  <Group gap="sm" mt="xs">
                    {canInteractEpisodeRow(episode.id) ? (
                      <>
                        <Anchor
                          component={Link}
                          to={`/dashboard/clients/${client.id}/episode-of-care/${episode.id}`}
                          size="sm"
                          fw={500}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          View Episode of Care
                        </Anchor>
                        {treatmentPlanDocumentIds.get(episode.id) ? (
                          <Anchor
                            component={Link}
                            to={`/dashboard/clients/${client.id}/chart-document/${treatmentPlanDocumentIds.get(episode.id)}`}
                            size="sm"
                            fw={500}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            View Treatment Plan
                          </Anchor>
                        ) : defaultTreatmentPlanTemplate?.id && canCreateTreatmentPlanForEpisode(episode) ? (
                          <Anchor
                            component="button"
                            type="button"
                            size="sm"
                            fw={500}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              const params = new URLSearchParams({
                                templateId: defaultTreatmentPlanTemplate.id!,
                                episodeId: episode.id,
                              });
                              navigate(`/dashboard/clients/${client.id}/chart-document/new?${params.toString()}`);
                            }}
                          >
                            Create Treatment Plan
                          </Anchor>
                        ) : null}
                      </>
                    ) : (
                      <Text size="sm" c="dimmed" fw={500}>
                        Episode details restricted
                      </Text>
                    )}
                  </Group>
                </div>
              </Paper>
            );
          })}
        </Stack>
      )}

      {followUpAndFinishedEpisodes.length > 0 && showFinishedEpisodes && (
        <>
          <Text size="sm" fw={600} c="gray.7" mt="lg" mb="sm">
            Finished
          </Text>
          <Stack gap="sm">
            {followUpAndFinishedEpisodes.map((episode) => {
              const isCurrentChartPrimary = episode.primaryClinicalPatient?.id === client.id;
              const primaryClientId = episode.primaryClinicalPatient?.id ?? client.id;
              return (
                <Paper
                  key={episode.id}
                  withBorder
                  p="md"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                  }}
                  onClick={() => {
                    navigate(`/dashboard/clients/${client.id}/episode-of-care/${episode.id}`);
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Group gap="xs" mb={2}>
                      <Text fw={500}>
                        {episode.name}
                      </Text>
                      <Badge color={getStatusColor(episode.status)} variant="light" size="sm">
                        {formatStatusText(episode.status)}
                      </Badge>
                      <Badge color={isCurrentChartPrimary ? 'blue' : 'gray'} variant="light" size="sm">
                        {isCurrentChartPrimary ? 'Primary' : 'Supporting'}
                      </Badge>
                    </Group>
                    <Text size="sm" c="gray.6">
                      Primary Provider: {episode.provider}
                    </Text>
                    <Text size="xs" c="gray.5">
                      {formatDate(episode.startDate)} – {episode.endDate ? formatDate(episode.endDate) : 'Present'}
                    </Text>
                    <Stack gap={0} mt={4}>
                      {episode.primaryClinicalPatient && (
                        <EpisodePartyRow
                          icon={<IconUsers size={13} />}
                          label="Primary Client"
                          party={episode.primaryClinicalPatient}
                          href={`/dashboard/clients/${episode.primaryClinicalPatient.id}`}
                        />
                      )}
                      {episode.groupMembers && episode.groupMembers.filter((m) => m.id !== episode.primaryClinicalPatient?.id).length > 0 && (
                        <Group gap="xs" mt={3} wrap="nowrap">
                          <Box style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }}><IconUsers size={13} /></Box>
                          <Text size="xs" c="gray.6" style={{ lineHeight: 1.4 }}>
                            <Text span fw={500} c="gray.7">Other Clients:</Text>{' '}
                            {episode.groupMembers
                              .filter((m) => m.id !== episode.primaryClinicalPatient?.id)
                              .map((m) => m.name)
                              .join(', ')}
                          </Text>
                        </Group>
                      )}
                      {episode.guarantor && (
                        <EpisodePartyRow
                          icon={<IconShield size={13} />}
                          label="Guarantor"
                          party={episode.guarantor}
                          href={`/dashboard/clients/${primaryClientId}/profile?tab=billing`}
                        />
                      )}
                    </Stack>
                    <Group gap="sm" mt="xs">
                      {canInteractEpisodeRow(episode.id) ? (
                        <>
                          <Anchor
                            component={Link}
                            to={`/dashboard/clients/${client.id}/episode-of-care/${episode.id}`}
                            size="sm"
                            fw={500}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            View Episode of Care
                          </Anchor>
                          {treatmentPlanDocumentIds.get(episode.id) ? (
                            <Anchor
                              component={Link}
                              to={`/dashboard/clients/${client.id}/chart-document/${treatmentPlanDocumentIds.get(episode.id)}`}
                              size="sm"
                              fw={500}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              View Treatment Plan
                            </Anchor>
                          ) : defaultTreatmentPlanTemplate?.id && canCreateTreatmentPlanForEpisode(episode) ? (
                            <Anchor
                              component="button"
                              type="button"
                              size="sm"
                              fw={500}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                const params = new URLSearchParams({
                                  templateId: defaultTreatmentPlanTemplate.id!,
                                  episodeId: episode.id,
                                });
                                navigate(`/dashboard/clients/${client.id}/chart-document/new?${params.toString()}`);
                              }}
                            >
                              Create Treatment Plan
                            </Anchor>
                          ) : null}
                        </>
                      ) : (
                        <Text size="sm" c="dimmed" fw={500}>
                          Episode details restricted
                        </Text>
                      )}
                    </Group>
                  </div>
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      {followUpAndFinishedEpisodes.length > 0 && (
        <Anchor
          component="button"
          type="button"
          size="sm"
          mt="sm"
          fw={500}
          onClick={() => setShowFinishedEpisodes((v) => !v)}
          style={{ alignSelf: 'flex-start' }}
        >
          {showFinishedEpisodes ? 'Hide finished episodes' : 'View finished episodes'}
        </Anchor>
      )}

      {episodes.length === 0 && (
        <Text c="gray.6" size="sm">
          No episodes of care
        </Text>
      )}
    </Stack>
  );

  // ─── Medications panel ────────────────────────────────────────────────
  const medicationsPanel = (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconPill size={18} color="gray" />
            <Title order={4}>Medications</Title>
            <LocalhostOnlyBadge />
          </Group>
          <Button
            leftSection={<IconPlus size={14} />}
            size="sm"
            onClick={() => {
              if (USE_LEGACY_ADD_MEDICATION_FORM) {
                openAddMedication();
              } else {
                openDoseSpotNewRx();
              }
            }}
          >
            Add medication
          </Button>
        </Group>
        {activeMedications.length === 0 ? (
          <Text c="gray.6" size="sm">
            No active medications
          </Text>
        ) : (
          <Stack gap="sm">
            {activeMedications.map((med) => (
              <Card
                key={med.id}
                withBorder
                style={{ cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                onClick={() => handleEditMedicationOpen(med)}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>{med.name}</Text>
                    <Text size="xs" c="gray.6">
                      {med.dosage} • {med.frequency}
                    </Text>
                  </div>
                  <Group gap={6} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                    <Badge color="green" variant="light" size="sm">
                      Active
                    </Badge>
                    <Menu position="bottom-end" shadow="md" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Medication actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => handleEditMedicationOpen(med)}
                        >
                          Edit medication
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => openDoseSpotRefill(med)}
                        >
                          Request refill
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconBan size={14} />}
                          color="orange"
                          onClick={() => openDoseSpotDiscontinue(med)}
                        >
                          Mark inactive
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Group>
                <Text size="xs" c="gray.6" mt="xs">
                  Prescribed by {med.prescribedBy} • {formatDate(med.startDate)}
                </Text>
                {med.notes && (
                  <Text size="xs" c="gray.6" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
                    Note: {med.notes}
                  </Text>
                )}
              </Card>
            ))}
          </Stack>
        )}
        {inactiveMedications.length > 0 && (
          <>
            <Anchor
              component="button"
              type="button"
              size="sm"
              fw={500}
              onClick={() => setShowInactiveMedications((v) => !v)}
              style={{ alignSelf: 'flex-start' }}
            >
              {showInactiveMedications ? 'Hide inactive medications' : 'View inactive medications'}
            </Anchor>
            {showInactiveMedications && (
              <Stack gap="sm">
                <Text size="sm" fw={600} c="gray.7">
                  Inactive
                </Text>
                {inactiveMedications.map((med) => (
                  <Card key={med.id} withBorder>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{med.name}</Text>
                        <Text size="xs" c="gray.6">
                          {med.dosage} • {med.frequency}
                        </Text>
                      </div>
                      <Badge color={getStatusColor(med.status)} variant="light" size="sm">
                        {formatStatusText(med.status)}
                      </Badge>
                    </Group>
                    <Text size="xs" c="gray.6" mt="xs">
                      Prescribed by {med.prescribedBy} • {formatDate(med.startDate)}
                      {med.endDate ? ` – ${formatDate(med.endDate)}` : ''}
                    </Text>
                  </Card>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Stack gap="xl">
      {filterRow}
      {hasRightColumn ? (
        <Group wrap="nowrap" gap="md" align="flex-start">
          <Box style={{ flex: '3 1 0%', minWidth: 0 }}>
            {medicalRecordPaper}
          </Box>
          <Stack gap="md" style={{ flex: '2 1 0%', minWidth: 0 }}>
            <Paper p="lg" withBorder>
              {episodesPanel}
            </Paper>
            {FEATURE_VISIBILITY.chartMedications ? medicationsPanel : null}
            {FEATURE_VISIBILITY.chartContacts ? (
              <ClientChartContactsPanel
                clientId={client.id}
                rows={chartContactRows}
                onRowActivate={handleChartContactRowActivate}
                localhostOnly
              />
            ) : null}
          </Stack>
        </Group>
      ) : (
        medicalRecordPaper
      )}

      {/* View Note Modal */}
      <Modal
        opened={viewOpened}
        onClose={handleCloseView}
        title={selectedNote ? (selectedNote.noteType === 'Risk Assessment' ? 'Risk assessment' : selectedNote.noteType === 'Safety Plan' ? 'Safety plan' : selectedNote.noteType === 'Transfer Request' ? 'Transfer request' : selectedNote.noteType === 'Chart Note' || selectedNote.noteType === 'Chart Notes' ? 'Chart note' : selectedNote.noteType.toLowerCase()) : 'Note'}
        size="xl"
        centered
        styles={{ content: { border: '1px solid #9CA3AF' } }}
      >
        {selectedNote && (
          <Stack gap="sm">
            {(selectedNote.groupSharedNoteReference || selectedNote.appointmentId) &&
              (selectedNote.therapyServiceType === 'group' ||
                selectedNote.groupSharedNoteReference ||
                selectedNote.serviceType?.toLowerCase().includes('group')) && (
                <GroupSharedNoteLink
                  sharedNoteReference={selectedNote.groupSharedNoteReference}
                  appointmentId={selectedNote.appointmentId}
                />
              )}
            <NoteDetailView
              note={localNotes.find((n) => n.id === selectedNote.id) ?? selectedNote}
              adminNotes={adminNotesForNote(selectedNote)}
              onAddAddendum={handleAddAddendum}
              onUpdateNote={handleUpdateNote}
              onSaveAndClose={handleCloseView}
              onEditAdminNote={handleEditAdminNoteOpen}
              onDeleteAdminNote={handleDeleteAdminNoteOpen}
            />
          </Stack>
        )}
      </Modal>

      <ClientContactFormModal
        opened={contactProfileOpened}
        onClose={() => {
          closeContactProfile();
          setContactProfileModalContact(null);
        }}
        mode="view"
        contact={contactProfileModalContact}
      />

      {/* Admin Note Detail Modal */}
      <Modal
        opened={viewAdminNoteOpened}
        onClose={() => { closeViewAdminNote(); setSelectedAdminNote(null); }}
        title={selectedAdminNote?.type ?? 'Admin note'}
        size="lg"
        centered
      >
        {selectedAdminNote && (
          <Stack gap="md">
            <Group gap="xs" align="center">
              <Text size="xs" c="gray.5">
                {formatFullDate(new Date(selectedAdminNote.createdAt))}
              </Text>
              <Text size="xs" c="gray.4">•</Text>
              <Text size="xs" c="gray.6">{selectedAdminNote.createdBy}</Text>
            </Group>
            <TextInput
              label="Title"
              value={editAdminNoteForm.title}
              onChange={(e) => setEditAdminNoteForm((prev) => ({ ...prev, title: e.currentTarget.value }))}
            />
            <Textarea
              label="Content"
              value={editAdminNoteForm.content}
              onChange={(e) => setEditAdminNoteForm((prev) => ({ ...prev, content: e.currentTarget.value }))}
              minRows={4}
              autosize
            />
            <Group justify="space-between">
              <Button
                variant="default"
                color="red"
                onClick={() => {
                  closeViewAdminNote();
                  handleDeleteAdminNoteOpen(selectedAdminNote);
                }}
              >
                Delete
              </Button>
              <Button
                onClick={handleSaveEditAdminNote}
                disabled={!editAdminNoteForm.title.trim() || !editAdminNoteForm.content.trim()}
              >
                Save changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <AddOtherDocumentModal
        opened={otherDocumentOpened}
        onClose={closeOtherDocument}
        onSelectTemplate={handleSelectChartDocumentTemplate}
      />

      {/* Add Note Modal */}
      <Modal
        opened={addNoteOpened}
        onClose={closeAddNote}
        title={
          addNoteKind === 'chart' ? 'Chart note' : 'Admin note'
        }
        size="lg"
        centered
      >
        {addNoteKind === 'chart' && (
          <AddChartNoteFormContent
            key={addNoteFormKey}
            onCancel={closeAddNote}
            onSave={handleSaveChartNote}
            activeEpisodes={activeEpisodes}
            inactiveEpisodes={followUpAndFinishedEpisodes}
            defaultEpisodeId={episodeFilter.length === 1 ? episodeFilter[0] : undefined}
          />
        )}
        {addNoteKind === 'admin' && mayCreateAdminNote && (
          <AddAdminNoteFormContent
            key={addNoteFormKey}
            onCancel={closeAddNote}
            onSave={handleSaveAdminNote}
            activeEpisodes={activeEpisodes}
            inactiveEpisodes={followUpAndFinishedEpisodes}
            defaultEpisodeId={episodeFilter.length === 1 ? episodeFilter[0] : undefined}
          />
        )}
      </Modal>

      {USE_LEGACY_ADD_MEDICATION_FORM && (
        <LegacyAddMedicationModal
          opened={addMedicationOpened}
          onClose={closeAddMedication}
          onAdd={handleLegacyAddMedication}
        />
      )}
      <DoseSpotMedicationShell
        opened={doseSpotOpened}
        onClose={closeDoseSpot}
        intent={doseSpotIntent}
        medication={doseSpotMedication}
        onDemoSessionComplete={handleDoseSpotDemoComplete}
      />

      {/* Edit Medication Modal */}
      <Modal
        opened={editMedicationOpened}
        onClose={() => { setSelectedMedication(null); closeEditMedication(); }}
        title="Edit medication"
        size="lg"
        centered
      >
        {selectedMedication && (
          <Stack gap="md">
            <TextInput
              label="Medication name"
              value={editMedForm.name}
              onChange={(e) => setEditMedForm((prev) => ({ ...prev, name: e.currentTarget.value }))}
              required
            />
            <Group grow>
              <TextInput
                label="Dosage"
                value={editMedForm.dosage}
                onChange={(e) => setEditMedForm((prev) => ({ ...prev, dosage: e.currentTarget.value }))}
              />
              <TextInput
                label="Frequency"
                value={editMedForm.frequency}
                onChange={(e) => setEditMedForm((prev) => ({ ...prev, frequency: e.currentTarget.value }))}
              />
            </Group>
            <TextInput
              label="Prescribed by"
              value={editMedForm.prescribedBy}
              onChange={(e) => setEditMedForm((prev) => ({ ...prev, prescribedBy: e.currentTarget.value }))}
            />
            <Group grow>
              <TextInput
                label="Start date"
                type="date"
                value={editMedForm.startDate}
                onChange={(e) => setEditMedForm((prev) => ({ ...prev, startDate: e.currentTarget.value }))}
              />
              <TextInput
                label="End date (optional)"
                type="date"
                value={editMedForm.endDate}
                onChange={(e) => setEditMedForm((prev) => ({ ...prev, endDate: e.currentTarget.value }))}
              />
            </Group>
            <Select
              label="Status"
              data={[
                { value: 'Active', label: 'Active' },
                { value: 'Discontinued', label: 'Discontinued' },
                { value: 'On Hold', label: 'On Hold' },
              ]}
              value={editMedForm.status}
              onChange={(v) => setEditMedForm((prev) => ({ ...prev, status: (v as Medication['status']) ?? 'Active' }))}
            />
            <Textarea
              label="Notes"
              value={editMedForm.notes}
              onChange={(e) => setEditMedForm((prev) => ({ ...prev, notes: e.currentTarget.value }))}
              minRows={2}
            />
            <Group justify="space-between">
              <Button
                color="red"
                variant="subtle"
                onClick={() => {
                  closeEditMedication();
                  handleDeleteMedication(selectedMedication);
                }}
              >
                Delete
              </Button>
              <Button onClick={handleSaveEditMedication} disabled={!editMedForm.name.trim()}>
                Save changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Medication Modal */}
      <Modal opened={deleteMedicationOpened} onClose={closeDeleteMedication} title="Delete medication" size="sm" centered>
        {selectedMedication && (
          <Stack gap="md">
            <Text size="sm" c="gray.7">
              Are you sure you want to remove <Text span fw={600}>{selectedMedication.name}</Text> from the medication list?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeDeleteMedication}>Cancel</Button>
              <Button color="red" onClick={confirmDeleteMedication}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Admin Note Modal */}
      <Modal opened={deleteAdminNoteOpened} onClose={closeDeleteAdminNote} title="Delete admin note" size="sm" centered>
        {selectedAdminNote && (
          <Stack gap="md">
            <Text size="sm" c="gray.7">
              Are you sure you want to delete this admin note?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeDeleteAdminNote}>Cancel</Button>
              <Button color="red" onClick={confirmDeleteAdminNote}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <FilterListDrawer
        opened={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Chart filters"
        sections={filterDrawerSections}
        value={filterDrawerValue}
        onChange={handleFilterDrawerChange}
        onClear={handleFilterDrawerClear}
      />

    </Stack>
  );
}
